/**
 * Settings page access control utility.
 *
 * Most Settings categories (Audit, Federation, IAM, System Config, Data
 * Export, Registry, Application Logs, Virtual MCP) are admin-only: every
 * backend route behind them requires is_admin with no granular UI
 * permission. The frontend mirrors that as a UX convenience layer.
 *
 * Governance is the exception (AGENTGOV-71): approve_service, approve_agent,
 * manage_governance_policy, and transfer_ownership are granular UI
 * permissions a non-admin can hold via an IAM group's ui_permissions, and
 * the backend already enforces them per-permission (see
 * registry/services/governance_permissions.py). A user holding any one of
 * them must be able to reach Settings and use the Governance category even
 * though they are not an admin.
 *
 * The ui_permissions from scopes.yml also control server/agent access
 * (e.g., list_service, toggle_service, list_agents, publish_agent) -- those
 * are unrelated to Settings visibility.
 */

interface SettingsUser {
  is_admin?: boolean;
  ui_permissions?: Record<string, string[] | undefined>;
}

// Granular UI permissions that grant a non-admin access to the Governance
// settings category. Holding any one of these for at least one resource is
// enough to reach the category; the specific tabs/actions each permission
// unlocks are still enforced by the backend per-request.
const GOVERNANCE_UI_PERMISSIONS = [
  'approve_service',
  'approve_agent',
  'manage_governance_policy',
  'transfer_ownership',
];

/**
 * Check if a user holds a given UI permission for at least one resource.
 */
function _hasUiPermission(user: SettingsUser | null, permission: string): boolean {
  if (!user) return false;
  const allowed = user.ui_permissions?.[permission];
  return Array.isArray(allowed) && allowed.length > 0;
}

/**
 * Check if a user can access the Governance settings category.
 * True for admins, or a user holding any governance UI permission.
 */
export function canAccessGovernance(user: SettingsUser | null): boolean {
  if (!user) return false;
  if (user.is_admin === true) return true;
  return GOVERNANCE_UI_PERMISSIONS.some((permission) => _hasUiPermission(user, permission));
}

/**
 * Check if a user can access the Settings page at all.
 * True for admins, or a user with access to at least one settings category.
 */
export function canAccessSettings(user: SettingsUser | null): boolean {
  if (!user) return false;
  if (user.is_admin === true) return true;
  return canAccessGovernance(user);
}
